import React, { useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';

/**
 * Viewer events reach every client in the clinic, so acting on each one individually would mean a
 * request per client per event. One bulk read covers every item at once, so it is throttled to
 * this interval instead — the same worst case as the 15s poll it replaces, but silent when nothing
 * is happening.
 */
const MIN_REFRESH_INTERVAL_MS = 15000;

/** Must match VIEW_EXPIRY in ViewerService: the server drops a viewer this long after their last ping. */
const VIEWER_TTL_MS = 20000;
/** How often expired viewers are swept locally. Bounds how long a stale badge can linger. */
const EXPIRY_SWEEP_MS = 5000;

/**
 * Factory function to create viewer provider configuration
 * @param {Object} config Configuration object
 * @param {string} config.itemType 'document' or 'task' (for logging)
 * @param {Function} config.fetchAllViewers async function(insightsService) => Promise<Array>
 * @param {Function} config.addViewerUpdateCallback function(notificationManager, callback) => unsubscribe
 * @returns {Object} Configuration object for ViewerProvider
 */
export const createViewerConfig = (config) => {
  return config;
};

/**
 * Factory function to create a ViewerProvider component with a specific context
 * @param {React.Context} ViewerContext - The React context to use
 * @param {Object} config - Viewer configuration
 * @returns {React.Component} ViewerProvider component
 */
export const createViewerProvider = (ViewerContext, config) => {
  return ({ children, notificationManager, insightsService, user }) => {
  const [itemViewers, setItemViewers] = useState(new Map()); // itemId -> array of viewers

  // itemId -> local time we last had evidence of a viewer.
  //
  // Stamped when an event arrives rather than from the viewer's server-side timestamp. A viewer
  // pings every 15s and every ping emits an event, so the event's arrival is the freshest possible
  // liveness signal — whereas the timestamp inside a throttled bulk read can already be 15s old,
  // which is what made a still-active viewer expire and the badge oscillate.
  const lastSeenRef = useRef(new Map());

  // One bulk read covering every item that currently has viewers. `seedLiveness` is for the very
  // first read, before any event has arrived: after that, events own liveness, and restamping here
  // would extend the life of a viewer who has already left.
  const fetchAllViewers = useCallback(async ({ seedLiveness = false } = {}) => {
    if (!insightsService) return;

    try {
      const allViewers = await config.fetchAllViewers(insightsService);

      // Backend sends array with 'id' field (not 'documentId' or 'taskId')
      const viewersMap = new Map();

      if (Array.isArray(allViewers)) {
        allViewers.forEach(itemViewersData => {
          if (itemViewersData.id && Array.isArray(itemViewersData.viewers)) {
            viewersMap.set(itemViewersData.id, itemViewersData.viewers);
          }
        });
      }

      if (seedLiveness) {
        const now = Date.now();
        viewersMap.forEach((_, itemId) => lastSeenRef.current.set(itemId, now));
      }

      setItemViewers(viewersMap);
    } catch (error) {
      console.error(`Error fetching ${config.itemType} viewers:`, error);
    }
  }, [insightsService]);

  // Refreshes at most once per interval however many events arrive. The first event in a quiet
  // period is served immediately; the rest collapse into one refresh at the interval boundary.
  const lastRefreshRef = useRef(0);
  const queuedRefreshRef = useRef(null);

  const requestRefresh = useCallback(() => {
    if (queuedRefreshRef.current) return;

    const wait = Math.max(0, MIN_REFRESH_INTERVAL_MS - (Date.now() - lastRefreshRef.current));
    queuedRefreshRef.current = setTimeout(() => {
      queuedRefreshRef.current = null;
      lastRefreshRef.current = Date.now();
      fetchAllViewers();
    }, wait);
  }, [fetchAllViewers]);

  useEffect(() => {
    if (!notificationManager) return;

    // The event says an item has a viewer right now, but not who. Liveness is taken from its
    // arrival; the throttled re-read is only for the names behind the count.
    const unsubscribe = config.addViewerUpdateCallback(notificationManager, (itemId) => {
      if (itemId) {
        lastSeenRef.current.set(itemId, Date.now());
      }
      requestRefresh();
    });

    return () => {
      unsubscribe();
      if (queuedRefreshRef.current) {
        clearTimeout(queuedRefreshRef.current);
        queuedRefreshRef.current = null;
      }
    };
  }, [notificationManager, requestRefresh]);

  // Server-side expiry is passive: it happens on the next read and emits no event, so a client
  // waiting for events alone would show "In Review" forever after the last viewer left. An item
  // whose events have stopped for longer than the TTL is dropped here instead. Costs nothing when
  // idle — the updater returns the previous map unchanged, so React skips the render.
  useEffect(() => {
    const sweep = setInterval(() => {
      const cutoff = Date.now() - VIEWER_TTL_MS;
      lastSeenRef.current.forEach((seenAt, itemId) => {
        if (seenAt < cutoff) {
          lastSeenRef.current.delete(itemId);
        }
      });

      setItemViewers(previous => {
        const next = new Map();
        let expired = false;

        previous.forEach((viewers, itemId) => {
          if (lastSeenRef.current.has(itemId)) {
            next.set(itemId, viewers);
          } else {
            expired = true;
          }
        });

        return expired ? next : previous;
      });
    }, EXPIRY_SWEEP_MS);

    return () => clearInterval(sweep);
  }, []);

  // Keyed on the id rather than the user object, which is not reference-stable across renders
  const userId = user?.userId;
  useEffect(() => {
    if (userId) {
      lastRefreshRef.current = Date.now();
      fetchAllViewers({ seedLiveness: true });
    } else {
      lastSeenRef.current.clear();
      setItemViewers(new Map());
    }
  }, [userId, fetchAllViewers]);

  // Check if an item has viewers
  const hasViewers = useCallback((itemId) => {
    const viewers = itemViewers.get(itemId);
    const result = Array.isArray(viewers) && viewers.length > 0;
    return result;
  }, [itemViewers]);

  // Get viewers for an item
  const getViewers = useCallback((itemId) => {
    return itemViewers.get(itemId) || [];
  }, [itemViewers]);

  // Get viewer count for an item
  const getViewerCount = useCallback((itemId) => {
    const viewers = itemViewers.get(itemId);
    return Array.isArray(viewers) ? viewers.length : 0;
  }, [itemViewers]);

  const contextValue = useMemo(() => ({
    itemViewers,
    hasViewers,
    getViewers,
    getViewerCount
  }), [itemViewers, hasViewers, getViewers, getViewerCount]);

    return (
      <ViewerContext.Provider value={contextValue}>
        {children}
      </ViewerContext.Provider>
    );
  };
};

/**
 * Hook factory to create item-type-specific hooks with a specific context
 * @param {React.Context} ViewerContext - The React context to use
 * @param {string} itemType - 'document' or 'task' (for error messages)
 * @returns {Function} useViewers hook
 */
export const createUseViewers = (ViewerContext, itemType) => {
  return () => {
    const context = useContext(ViewerContext);
    if (!context) {
      throw new Error(`use${itemType.charAt(0).toUpperCase() + itemType.slice(1)}Viewers must be used within a ViewerProvider`);
    }
    return context;
  };
};
